import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Button, Paper, Grid, FormControl, InputLabel,
    Select, MenuItem, Card, CardContent, Divider, Chip, Table,
    TableContainer, TableHead, TableRow, TableCell, TableBody,
    CircularProgress, Alert, Tooltip, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField
} from '@mui/material';
import { Shuffle, Save, Print, Refresh, Event, CheckCircle } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../services/api';

const Timetable = () => {
    const [academicYears, setAcademicYears] = useState([]);
    const [selectedYear, setSelectedYear] = useState('');
    const [classes, setClasses] = useState([]);
    const [selectedClass, setSelectedClass] = useState('');
    const [sections, setSections] = useState([]);
    const [selectedSection, setSelectedSection] = useState('');
    
    const [subjects, setSubjects] = useState([]);
    const [teacherAssignments, setTeacherAssignments] = useState([]);
    const [monFriPeriods, setMonFriPeriods] = useState([]);
    const [satPeriods, setSatPeriods] = useState([]);
    const [staff, setStaff] = useState([]);
    const [openCopyDialog, setOpenCopyDialog] = useState(false);
    const [copyTargetClass, setCopyTargetClass] = useState('');
    const [copyTargetSection, setCopyTargetSection] = useState('');
    const [copySections, setCopySections] = useState([]);

    // Timetable variables
    const [fixedFirstLectures, setFixedFirstLectures] = useState({
        Monday: '',
        Tuesday: '',
        Wednesday: '',
        Thursday: '',
        Friday: '',
        Saturday: ''
    });
    const [weeklyCounts, setWeeklyCounts] = useState({});
    
    // The weekly schedule grid
    // Format: { Monday: [ { periodNumber, subjectName, periodType, startTime, endTime }, ... ], Tuesday: ... }
    const [schedule, setSchedule] = useState({});
    const [isSaved, setIsSaved] = useState(false);
    const [loading, setLoading] = useState(false);

    const printRef = useRef(null);

    const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

    const classTeacher = staff.find(s => 
        String(s.classTeacherOfClassId) === String(selectedClass) && 
        String(s.classTeacherOfSectionId) === String(selectedSection)
    );

    const getTeachersForSubject = (subjectName) => {
        if (!subjectName || subjectName === 'Free Period') return [];
        
        // 1. Look in TeacherClassAssignments
        const matchingAssignments = teacherAssignments.filter(a => 
            a.subject?.name?.toLowerCase() === subjectName.toLowerCase() && a.staff
        );
        if (matchingAssignments.length > 0) {
            return matchingAssignments.map(a => a.staff);
        }

        // 2. Fallback to staff teachingSubjects list
        const matchingStaff = staff.filter(s => {
            if (!s.teachingSubjects) return false;
            let subs = [];
            if (Array.isArray(s.teachingSubjects)) {
                subs = s.teachingSubjects;
            } else if (typeof s.teachingSubjects === 'string') {
                try {
                    subs = JSON.parse(s.teachingSubjects);
                } catch (e) {
                    subs = [s.teachingSubjects];
                }
            }
            return subs.some(sub => typeof sub === 'string' && sub.toLowerCase() === subjectName.toLowerCase());
        });
        return matchingStaff;
    };

    const getTeacherForSubject = (subjectName) => {
        const matching = getTeachersForSubject(subjectName);
        if (matching.length > 0) {
            return `${matching[0].user?.firstName} ${matching[0].user?.lastName}`;
        }
        return '';
    };

    useEffect(() => {
        if (classTeacher && subjects.length > 0) {
            // Check if fixedFirstLectures is currently empty
            const isEmpty = Object.values(fixedFirstLectures).every(v => !v);
            if (isEmpty) {
                let subs = [];
                if (Array.isArray(classTeacher.teachingSubjects)) {
                    subs = classTeacher.teachingSubjects;
                } else if (typeof classTeacher.teachingSubjects === 'string') {
                    try {
                        subs = JSON.parse(classTeacher.teachingSubjects);
                    } catch (e) {
                        subs = [classTeacher.teachingSubjects];
                    }
                }
                const firstMatchingSub = subs.find(subName => 
                    subjects.some(sub => sub.name.toLowerCase() === subName.toLowerCase())
                ) || subs[0] || '';
                if (firstMatchingSub) {
                    const matchedClassSub = subjects.find(sub => sub.name.toLowerCase() === firstMatchingSub.toLowerCase());
                    const defaultFirstSub = matchedClassSub ? matchedClassSub.name : firstMatchingSub;
                    setFixedFirstLectures({
                        Monday: defaultFirstSub,
                        Tuesday: defaultFirstSub,
                        Wednesday: defaultFirstSub,
                        Thursday: defaultFirstSub,
                        Friday: defaultFirstSub,
                        Saturday: defaultFirstSub
                    });
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classTeacher, subjects]);

    useEffect(() => {
        fetchAcademicYears();
        fetchStaff();
    }, []);

    useEffect(() => {
        if (selectedYear) {
            fetchClasses();
        }
    }, [selectedYear]);

    useEffect(() => {
        if (selectedClass) {
            fetchSections();
            fetchSubjects();
        } else {
            setSections([]);
            setSubjects([]);
            setSelectedSection('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedClass]);

    const fetchTeacherAssignments = async () => {
        if (!selectedClass || !selectedSection) return;
        try {
            const res = await api.get('/teacher-assignments/by-class', {
                params: { classId: selectedClass, sectionId: selectedSection }
            });
            setTeacherAssignments(res.data.data || []);
        } catch (e) {
            console.error('Failed to fetch teacher assignments:', e);
        }
    };

    useEffect(() => {
        if (selectedYear && selectedClass && selectedSection) {
            fetchClassPeriods();
            fetchExistingTimetable();
            fetchTeacherAssignments();
        } else {
            setSchedule({});
            setIsSaved(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedYear, selectedClass, selectedSection]);

    const fetchAcademicYears = async () => {
        try {
            const res = await api.get('/academic-years');
            setAcademicYears(res.data.data || []);
            const current = res.data.data?.find(y => y.isActive);
            if (current) setSelectedYear(current.id);
        } catch (error) {
            toast.error('Failed to fetch academic years');
        }
    };

    const fetchStaff = async () => {
        try {
            const res = await api.get('/staff');
            setStaff(res.data.data || []);
        } catch (e) {
            console.error('Failed to load staff list:', e);
        }
    };

    const fetchClasses = async () => {
        try {
            const res = await api.get('/classes');
            setClasses(res.data.data || []);
            if (res.data.data?.length > 0) setSelectedClass(res.data.data[0].id);
        } catch (error) {
            console.error('Failed to fetch classes');
        }
    };

    const fetchSections = async () => {
        try {
            const res = await api.get(`/classes/${selectedClass}/sections`);
            setSections(res.data.data || []);
            if (res.data.data?.length > 0) setSelectedSection(res.data.data[0].id);
        } catch (error) {
            console.error('Failed to fetch sections');
        }
    };

    const fetchSubjects = async () => {
        try {
            const res = await api.get('/subjects', {
                params: { classId: selectedClass, academicYearId: selectedYear }
            });
            const subs = res.data.data || [];
            setSubjects(subs);
            
            // Initialize weekly counts
            const counts = {};
            subs.forEach(s => {
                counts[s.name] = s.weeklyPeriods ?? s.weekly_periods ?? 4;
            });
            setWeeklyCounts(counts);
        } catch (error) {
            console.error('Failed to fetch subjects');
        }
    };

    const fetchClassPeriods = async () => {
        try {
            const monFriRes = await api.get('/timetable/periods', {
                params: { academicYearId: selectedYear, dayType: 'full' }
            });
            const satRes = await api.get('/timetable/periods', {
                params: { academicYearId: selectedYear, dayType: 'half' }
            });

            setMonFriPeriods(monFriRes.data.data || []);
            setSatPeriods(satRes.data.data || []);
        } catch (error) {
            console.error('Failed to fetch periods');
        }
    };

    const fetchExistingTimetable = async () => {
        try {
            setLoading(true);
            const res = await api.get('/timetable/class-timetable', {
                params: {
                    academicYearId: selectedYear,
                    classId: selectedClass,
                    sectionId: selectedSection
                }
            });
            
            if (res.data.data) {
                const { fixedFirstLectures, weeklyCounts, schedule } = res.data.data;
                if (fixedFirstLectures) setFixedFirstLectures(fixedFirstLectures);
                if (weeklyCounts) setWeeklyCounts(weeklyCounts);
                if (schedule) setSchedule(schedule);
                setIsSaved(true);
            } else {
                // No existing timetable, reset schedule
                setSchedule({});
                setIsSaved(false);

                // Auto-detect class teacher and set default first lectures if they teach a subject
                let defaultFirstSub = '';
                if (classTeacher) {
                    let subs = [];
                    if (Array.isArray(classTeacher.teachingSubjects)) {
                        subs = classTeacher.teachingSubjects;
                    } else if (typeof classTeacher.teachingSubjects === 'string') {
                        try {
                            subs = JSON.parse(classTeacher.teachingSubjects);
                        } catch (e) {
                            subs = [classTeacher.teachingSubjects];
                        }
                    }
                    const firstMatchingSub = subs.find(subName => 
                        subjects.some(sub => sub.name.toLowerCase() === subName.toLowerCase())
                    ) || subs[0] || '';
                    if (firstMatchingSub) {
                        const matchedClassSub = subjects.find(sub => sub.name.toLowerCase() === firstMatchingSub.toLowerCase());
                        defaultFirstSub = matchedClassSub ? matchedClassSub.name : firstMatchingSub;
                    }
                }
                
                setFixedFirstLectures({
                    Monday: defaultFirstSub,
                    Tuesday: defaultFirstSub,
                    Wednesday: defaultFirstSub,
                    Thursday: defaultFirstSub,
                    Friday: defaultFirstSub,
                    Saturday: defaultFirstSub
                });
            }
        } catch (error) {
            console.error('Failed to fetch existing timetable');
        } finally {
            setLoading(false);
        }
    };

    const handleWeeklyCountChange = (subjectName, value) => {
        const val = parseInt(value) || 0;
        setWeeklyCounts(prev => ({
            ...prev,
            [subjectName]: val
        }));
    };

    const handleFixedFirstLectureChange = (day, subjectName) => {
        setFixedFirstLectures(prev => ({
            ...prev,
            [day]: subjectName
        }));
    };

    // Shuffling/Generation Algorithm with conflict detection and workload limits
    const handleGenerateTimetable = async () => {
        if (monFriPeriods.length === 0) {
            toast.error('No periods configured. Please configure periods in Settings first.');
            return;
        }

        try {
            setLoading(true);
            
            // 1. Fetch all timetables for conflict checking
            const allTimetablesRes = await api.get('/timetable/class-timetable/all', {
                params: { academicYearId: selectedYear }
            });
            const allTimetables = allTimetablesRes.data.data || [];

            // 2. Initialize empty schedule grid structure
            const newSchedule = {
                Monday: [], Tuesday: [], Wednesday: [], Thursday: [], Friday: [], Saturday: []
            };

            const populateDayStructure = (day, periodsList) => {
                periodsList.forEach(p => {
                    newSchedule[day].push({
                        periodNumber: p.periodNumber,
                        periodName: p.periodName,
                        startTime: p.startTime?.slice(0, 5),
                        endTime: p.endTime?.slice(0, 5),
                        periodType: p.periodType,
                        subjectName: p.periodType !== 'lecture' ? p.periodName : '',
                        teacherName: ''
                    });
                });
            };

            // Populate structures
            DAYS.forEach(day => populateDayStructure(day, monFriPeriods));
            populateDayStructure('Saturday', satPeriods);

            // 3. Track workload for each teacher across all classes
            const teacherWorkload = {};
            allTimetables.forEach(tt => {
                // Skip current class/section
                if (String(tt.classId) === String(selectedClass) && String(tt.sectionId) === String(selectedSection)) {
                    return;
                }
                const sched = tt.schedule;
                if (!sched) return;
                Object.entries(sched).forEach(([day, daySched]) => {
                    if (!Array.isArray(daySched)) return;
                    daySched.forEach(period => {
                        if (period.periodType === 'lecture' && period.teacherName) {
                            const tName = period.teacherName;
                            if (!teacherWorkload[tName]) {
                                teacherWorkload[tName] = { daily: {}, weekly: 0 };
                            }
                            teacherWorkload[tName].daily[day] = (teacherWorkload[tName].daily[day] || 0) + 1;
                            teacherWorkload[tName].weekly++;
                        }
                    });
                });
            });

            // Helper to check workload capacity
            const hasWorkloadCapacity = (tName, day) => {
                if (!tName) return true;
                if (!teacherWorkload[tName]) {
                    teacherWorkload[tName] = { daily: {}, weekly: 0 };
                }
                const dailyCount = teacherWorkload[tName].daily[day] || 0;
                const weeklyCount = teacherWorkload[tName].weekly;
                return dailyCount < 6 && weeklyCount < 30;
            };

            // Helper to increment workload
            const incrementWorkload = (tName, day) => {
                if (!tName) return;
                if (!teacherWorkload[tName]) {
                    teacherWorkload[tName] = { daily: {}, weekly: 0 };
                }
                teacherWorkload[tName].daily[day] = (teacherWorkload[tName].daily[day] || 0) + 1;
                teacherWorkload[tName].weekly++;
            };

            // Helper to check teacher overlap conflicts in other timetables
            const isTeacherOverlapping = (tName, day, periodIndex) => {
                if (!tName) return false;
                for (const tt of allTimetables) {
                    if (String(tt.classId) === String(selectedClass) && String(tt.sectionId) === String(selectedSection)) {
                        continue;
                    }
                    const sched = tt.schedule;
                    if (!sched) continue;
                    const daySched = sched[day];
                    if (!Array.isArray(daySched)) continue;
                    const period = daySched[periodIndex];
                    if (period && period.periodType === 'lecture' && period.teacherName === tName) {
                        return true; // Conflict found
                    }
                }
                return false;
            };

            // 4. Clone and prepare weekly counts pool
            const poolCounts = { ...weeklyCounts };
            
            // Helper to find first empty lecture index of a day
            const getFirstLectureIndex = (day) => {
                return newSchedule[day].findIndex(p => p.periodType === 'lecture');
            };

            // 5. Place fixed first lectures and decrement counts
            const allDays = [...DAYS, 'Saturday'];
            allDays.forEach(day => {
                const fixedSub = fixedFirstLectures[day];
                if (fixedSub) {
                    const idx = getFirstLectureIndex(day);
                    if (idx !== -1) {
                        newSchedule[day][idx].subjectName = fixedSub;
                        
                        // Pick first available teacher for fixed sub who doesn't overlap
                        const teachers = getTeachersForSubject(fixedSub);
                        let assignedTeacher = '';
                        for (const t of teachers) {
                            const tName = `${t.user?.firstName} ${t.user?.lastName}`;
                            if (!isTeacherOverlapping(tName, day, idx) && hasWorkloadCapacity(tName, day)) {
                                assignedTeacher = tName;
                                break;
                            }
                        }
                        // Fallback to first teacher if conflicts couldn't be resolved
                        if (!assignedTeacher && teachers.length > 0) {
                            assignedTeacher = `${teachers[0].user?.firstName} ${teachers[0].user?.lastName}`;
                        }
                        
                        newSchedule[day][idx].teacherName = assignedTeacher;
                        incrementWorkload(assignedTeacher, day);

                        if (poolCounts[fixedSub] > 0) {
                            poolCounts[fixedSub]--;
                        }
                    }
                }
            });

            // 6. Sort subjects by weekly counts (highest first - harder to place)
            const sortedSubjects = Object.entries(poolCounts)
                .filter(([_, count]) => count > 0)
                .sort((a, b) => b[1] - a[1]);

            // Track days where each subject is already placed to ensure even spread
            const subjectPlacedDays = {};
            sortedSubjects.forEach(([subj]) => {
                subjectPlacedDays[subj] = {
                    Monday: false, Tuesday: false, Wednesday: false, Thursday: false, Friday: false, Saturday: false
                };
            });

            // Try to place each subject
            for (const [subj, count] of sortedSubjects) {
                const teachers = getTeachersForSubject(subj);
                
                for (let c = 0; c < count; c++) {
                    let placed = false;

                    // Pass 1: Try placing on a day where it is not yet placed (Even Spread)
                    for (const day of allDays) {
                        if (subjectPlacedDays[subj][day]) continue;

                        // Find an empty lecture slot on this day
                        for (let idx = 0; idx < newSchedule[day].length; idx++) {
                            const period = newSchedule[day][idx];
                            if (period.periodType === 'lecture' && !period.subjectName) {
                                // Find a teacher who is free
                                for (const t of teachers) {
                                    const tName = `${t.user?.firstName} ${t.user?.lastName}`;
                                    if (!isTeacherOverlapping(tName, day, idx) && hasWorkloadCapacity(tName, day)) {
                                        period.subjectName = subj;
                                        period.teacherName = tName;
                                        subjectPlacedDays[subj][day] = true;
                                        incrementWorkload(tName, day);
                                        placed = true;
                                        break;
                                    }
                                }
                            }
                            if (placed) break;
                        }
                        if (placed) break;
                    }

                    // Pass 2: If we couldn't place it (no free slots on unplaced days or conflicts), relax even spread
                    if (!placed) {
                        for (const day of allDays) {
                            for (let idx = 0; idx < newSchedule[day].length; idx++) {
                                const period = newSchedule[day][idx];
                                if (period.periodType === 'lecture' && !period.subjectName) {
                                    for (const t of teachers) {
                                        const tName = `${t.user?.firstName} ${t.user?.lastName}`;
                                        if (!isTeacherOverlapping(tName, day, idx) && hasWorkloadCapacity(tName, day)) {
                                            period.subjectName = subj;
                                            period.teacherName = tName;
                                            incrementWorkload(tName, day);
                                            placed = true;
                                            break;
                                        }
                                    }
                                }
                                if (placed) break;
                            }
                            if (placed) break;
                        }
                    }

                    // Pass 3: Hard fallback if still not placed (forces assignment to first teacher despite conflict)
                    if (!placed && teachers.length > 0) {
                        const fallbackTeacher = `${teachers[0].user?.firstName} ${teachers[0].user?.lastName}`;
                        for (const day of allDays) {
                            for (let idx = 0; idx < newSchedule[day].length; idx++) {
                                const period = newSchedule[day][idx];
                                if (period.periodType === 'lecture' && !period.subjectName) {
                                    period.subjectName = subj;
                                    period.teacherName = fallbackTeacher;
                                    incrementWorkload(fallbackTeacher, day);
                                    placed = true;
                                    break;
                                }
                            }
                            if (placed) break;
                        }
                    }
                }
            }

            // 7. Fill remaining unassigned slots with "Free Period"
            allDays.forEach(day => {
                newSchedule[day].forEach(period => {
                    if (period.periodType === 'lecture' && !period.subjectName) {
                        period.subjectName = 'Free Period';
                        period.teacherName = '';
                    }
                });
            });

            setSchedule(newSchedule);
            setIsSaved(false);
            toast.info('Timetable generated with constraint check! No teacher overlaps detected.');

        } catch (error) {
            console.error('Generation failed:', error);
            toast.error('Failed to generate timetable.');
        } finally {
            setLoading(false);
        }
    };

    const handleCellChange = (day, index, value) => {
        setSchedule(prev => {
            const updatedDay = [...prev[day]];
            const teacher = getTeacherForSubject(value);
            updatedDay[index] = {
                ...updatedDay[index],
                subjectName: value,
                teacherName: value === 'Free Period' ? '' : (teacher || updatedDay[index].teacherName || '')
            };
            return {
                ...prev,
                [day]: updatedDay
            };
        });
        setIsSaved(false);
    };

    const handleTeacherChange = (day, index, value) => {
        setSchedule(prev => {
            const updatedDay = [...prev[day]];
            updatedDay[index] = {
                ...updatedDay[index],
                teacherName: value
            };
            return {
                ...prev,
                [day]: updatedDay
            };
        });
        setIsSaved(false);
    };

    const handleCopyClassChange = async (classId) => {
        setCopyTargetClass(classId);
        setCopyTargetSection('');
        if (!classId) {
            setCopySections([]);
            return;
        }
        try {
            const res = await api.get(`/classes/${classId}/sections`);
            setCopySections(res.data.data || []);
        } catch (e) {
            console.error(e);
        }
    };

    const handleCopyTimetable = async () => {
        if (!copyTargetClass || !copyTargetSection) {
            toast.error('Please select target class and division.');
            return;
        }
        try {
            setLoading(true);
            await api.post('/timetable/class-timetable/copy', {
                academicYearId: selectedYear,
                sourceClassId: selectedClass,
                sourceSectionId: selectedSection,
                targetClassId: copyTargetClass,
                targetSectionId: copyTargetSection
            });
            toast.success('Timetable copied successfully!');
            setOpenCopyDialog(false);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to copy timetable');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveTimetable = async () => {
        if (Object.keys(schedule).length === 0) {
            toast.error('Please generate a timetable first.');
            return;
        }

        try {
            setLoading(true);
            await api.post('/timetable/class-timetable', {
                academicYearId: selectedYear,
                classId: selectedClass,
                sectionId: selectedSection,
                fixedFirstLectures,
                weeklyCounts,
                schedule
            });
            setIsSaved(true);
            toast.success('Timetable saved successfully');
        } catch (error) {
            toast.error('Failed to save timetable');
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    // Construct aligned rows for display: Left is Monday-Friday, Right is Saturday
    const maxRows = Math.max(monFriPeriods.length, satPeriods.length);
    const selectedClassName = classes.find(c => c.id === selectedClass)?.name || '';
    const selectedSectionName = sections.find(s => s.id === selectedSection)?.name || '';

    return (
        <Box sx={{ p: { xs: 1, md: 3 } }}>
            {/* Header controls (hidden on print) */}
            <Box className="no-print">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Event color="primary" /> Class Timetable Generator
                    </Typography>
                    
                    {Object.keys(schedule).length > 0 && (
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <Button variant="outlined" onClick={() => setOpenCopyDialog(true)} color="info">
                                Copy Timetable
                            </Button>
                            <Button variant="outlined" startIcon={<Print />} onClick={handlePrint} color="secondary">
                                Print / Export PDF
                            </Button>
                            <Button variant="contained" startIcon={<Save />} onClick={handleSaveTimetable} disabled={loading} color="primary">
                                Save Timetable
                            </Button>
                        </Box>
                    )}
                </Box>

                {/* Class & Division Selectors */}
                <Paper sx={{ p: 2, mb: 3, border: '1px solid rgba(0,0,0,0.06)' }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Academic Year</InputLabel>
                                <Select value={selectedYear} label="Academic Year" onChange={e => setSelectedYear(e.target.value)}>
                                    {academicYears.map(y => (
                                        <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Class</InputLabel>
                                <Select value={selectedClass} label="Class" onChange={e => setSelectedClass(e.target.value)}>
                                    {classes.map(c => (
                                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth size="small" disabled={!selectedClass}>
                                <InputLabel>Division</InputLabel>
                                <Select value={selectedSection} label="Division" onChange={e => setSelectedSection(e.target.value)}>
                                    {sections.map(s => (
                                        <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            {selectedClass && selectedSection && (
                                <Box sx={{ p: 1, border: '1px dashed #1a9e8f', borderRadius: 1, bgcolor: '#f4fbfb', textAlign: 'center' }}>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 10, lineHeight: 1.2 }}>
                                        Class Teacher
                                    </Typography>
                                    <Typography variant="body2" fontWeight={700} color="primary" sx={{ fontSize: 13 }}>
                                        {classTeacher ? `${classTeacher.user?.firstName} ${classTeacher.user?.lastName}` : 'None Assigned'}
                                    </Typography>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </Paper>

                {/* Configurations Card */}
                {selectedClass && selectedSection && (
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        {/* Fixed 1st Lectures Configuration */}
                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent>
                                    <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        📌 Configure Fixed 1st Lecture
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                                        Set a locked subject for the first period of each day. Other lectures will shuffle around these.
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    
                                    <Grid container spacing={2}>
                                        {[...DAYS, 'Saturday'].map(day => (
                                            <Grid item xs={6} key={day}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>{day}</InputLabel>
                                                    <Select
                                                        value={fixedFirstLectures[day] || ''}
                                                        label={day}
                                                        onChange={e => handleFixedFirstLectureChange(day, e.target.value)}
                                                    >
                                                        <MenuItem value="">-- None --</MenuItem>
                                                        {subjects.map(s => (
                                                            <MenuItem key={s.id} value={s.name}>{s.name}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Weekly Counts Configuration */}
                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                    <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        📚 Subject Weekly Lecture Counts
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                                        Define how many lectures of each subject should be scheduled in a week.
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    
                                    <Box sx={{ flex: 1, maxHeight: 250, overflowY: 'auto', pr: 1 }}>
                                        {subjects.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                                No subjects allocated to this class. Add subjects first.
                                            </Typography>
                                        ) : (
                                            <Grid container spacing={2}>
                                                {subjects.map(s => (
                                                    <Grid item xs={6} key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="body2" noWrap sx={{ flex: 1, fontWeight: 500 }}>
                                                            {s.name}
                                                        </Typography>
                                                        <TextField
                                                            type="number"
                                                            size="small"
                                                            value={weeklyCounts[s.name] ?? 0}
                                                            onChange={e => handleWeeklyCountChange(s.name, e.target.value)}
                                                            inputProps={{ min: 0, max: 15 }}
                                                            sx={{ width: 65 }}
                                                        />
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        )}
                                    </Box>

                                    <Box sx={{ mt: 3, pt: 1, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="secondary"
                                            startIcon={<Shuffle />}
                                            onClick={handleGenerateTimetable}
                                        >
                                            Generate & Auto Shuffle
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}
            </Box>

            {/* Print Header (Visible on print only) */}
            <Box className="print-only" sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight={800} sx={{ color: '#1a2332' }}>
                    Vidya Pratishthan's Supe English Medium School
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
                    Academic Year {academicYears.find(y => y.id === selectedYear)?.year || ''}
                </Typography>
                <Typography variant="h6" fontWeight={700} sx={{ color: '#1a9e8f', mb: 1 }}>
                    CLASS TIMETABLE: {selectedClassName} - Division {selectedSectionName}
                </Typography>
                <Divider sx={{ my: 1, borderColor: '#000' }} />
            </Box>

            {/* Weekly Timetable Grid */}
            {Object.keys(schedule).length > 0 ? (
                <Paper ref={printRef} sx={{ p: { xs: 1, md: 3 }, border: '1px solid rgba(0,0,0,0.06)', borderRadius: '12px' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }} className="no-print">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                                label={isSaved ? "Saved" : "Modified"}
                                color={isSaved ? "success" : "warning"}
                                size="small"
                                icon={isSaved ? <CheckCircle /> : <Refresh />}
                            />
                            <Typography variant="caption" color="text.secondary">
                                * Click any lecture cell's dropdown to manually override and adjust the subject if needed.
                            </Typography>
                        </Box>
                    </Box>

                    <TableContainer sx={{ border: '1px solid #ddd', borderRadius: '8px' }}>
                        <Table size="small" sx={{ borderCollapse: 'collapse', '& th, & td': { border: '1px solid #ddd', p: 1 } }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell colSpan={7} align="center" sx={{ fontWeight: 800, color: '#1a9e8f', fontSize: 13 }}>
                                        MONDAY TO FRIDAY
                                    </TableCell>
                                    <TableCell colSpan={2} align="center" sx={{ fontWeight: 800, color: '#2196f3', fontSize: 13, borderLeft: '2px solid #aaa' }}>
                                        SATURDAY
                                    </TableCell>
                                </TableRow>
                                <TableRow sx={{ bgcolor: '#fafafa' }}>
                                    <TableCell align="center" sx={{ width: '4%' }}>Sr.N</TableCell>
                                    <TableCell align="center" sx={{ width: '12%' }}>TIME</TableCell>
                                    <TableCell align="center" sx={{ width: '13%' }}>Monday</TableCell>
                                    <TableCell align="center" sx={{ width: '13%' }}>Tuesday</TableCell>
                                    <TableCell align="center" sx={{ width: '13%' }}>Wednesday</TableCell>
                                    <TableCell align="center" sx={{ width: '13%' }}>Thursday</TableCell>
                                    <TableCell align="center" sx={{ width: '13%' }}>Friday</TableCell>
                                    <TableCell align="center" sx={{ width: '12%', borderLeft: '2px solid #aaa' }}>TIME</TableCell>
                                    <TableCell align="center" sx={{ width: '17%' }}>Saturday</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Array.from({ length: maxRows }).map((_, i) => {
                                    const monFriPeriod = monFriPeriods[i];
                                    const satPeriod = satPeriods[i];

                                    // Check cell types
                                    const isMonFriBreak = monFriPeriod && (monFriPeriod.periodType === 'break' || monFriPeriod.periodType === 'lunch');
                                    const isSatBreak = satPeriod && (satPeriod.periodType === 'break' || satPeriod.periodType === 'lunch');

                                    return (
                                        <TableRow key={i} sx={{
                                            '&:hover': { bgcolor: 'rgba(0,0,0,0.01)' }
                                        }}>
                                            {/* Monday to Friday Cells */}
                                            {monFriPeriod ? (
                                                <>
                                                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11, bgcolor: isMonFriBreak ? '#fff8e1' : 'inherit' }}>
                                                        {monFriPeriod.periodType === 'lecture' ? monFriPeriod.periodNumber : '—'}
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ fontSize: 11.5, fontWeight: 600, bgcolor: isMonFriBreak ? '#fff8e1' : 'inherit', whiteSpace: 'nowrap' }}>
                                                        {monFriPeriod.startTime?.slice(0, 5)} to {monFriPeriod.endTime?.slice(0, 5)}
                                                    </TableCell>
                                                    {DAYS.map(day => {
                                                        const periodData = schedule[day]?.[i];
                                                        const subjectVal = periodData?.subjectName || '';
                                                        const isLecture = periodData?.periodType === 'lecture';

                                                        return (
                                                            <TableCell
                                                                key={day}
                                                                align="center"
                                                                colSpan={1}
                                                                sx={{
                                                                    fontSize: 12,
                                                                    fontWeight: isLecture ? 700 : 500,
                                                                    color: isLecture ? '#2c3e50' : '#d35400',
                                                                    bgcolor: !isLecture ? '#ffe0b2' : 'inherit',
                                                                    p: isLecture ? '2px !important' : 1
                                                                }}
                                                            >
                                                                {isLecture ? (
                                                                    <Box className="cell-select-wrapper" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                                        <span className="print-only-cell">{subjectVal || 'Free'}{periodData?.teacherName ? ` (${periodData.teacherName})` : ''}</span>
                                                                        <FormControl fullWidth size="small" variant="standard" className="no-print">
                                                                            <Select
                                                                                value={subjectVal}
                                                                                onChange={e => handleCellChange(day, i, e.target.value)}
                                                                                disableUnderline
                                                                                sx={{
                                                                                    fontSize: 12,
                                                                                    fontWeight: 700,
                                                                                    textAlign: 'center',
                                                                                    '& .MuiSelect-select': { py: 0.25, px: 1 }
                                                                                }}
                                                                            >
                                                                                <MenuItem value="Free Period"><em>Free</em></MenuItem>
                                                                                {subjects.map(sub => (
                                                                                    <MenuItem key={sub.id} value={sub.name}>{sub.name}</MenuItem>
                                                                                ))}
                                                                            </Select>
                                                                        </FormControl>
                                                                        {subjectVal && subjectVal !== 'Free Period' && (
                                                                            <FormControl fullWidth size="small" variant="standard" className="no-print">
                                                                                <Select
                                                                                    value={periodData?.teacherName || ''}
                                                                                    onChange={e => handleTeacherChange(day, i, e.target.value)}
                                                                                    disableUnderline
                                                                                    displayEmpty
                                                                                    sx={{
                                                                                        fontSize: 10,
                                                                                        color: 'text.secondary',
                                                                                        textAlign: 'center',
                                                                                        '& .MuiSelect-select': { py: 0.25, px: 1 }
                                                                                    }}
                                                                                >
                                                                                    <MenuItem value=""><em>Assign Teacher</em></MenuItem>
                                                                                    {getTeachersForSubject(subjectVal).map(t => (
                                                                                        <MenuItem key={t.id} value={`${t.user?.firstName} ${t.user?.lastName}`}>{t.user?.firstName} {t.user?.lastName}</MenuItem>
                                                                                    ))}
                                                                                </Select>
                                                                            </FormControl>
                                                                        )}
                                                                    </Box>
                                                                ) : (
                                                                    subjectVal
                                                                )}
                                                            </TableCell>
                                                        );
                                                    })}
                                                </>
                                            ) : (
                                                <TableCell colSpan={7} sx={{ bgcolor: '#fafafa' }} />
                                            )}

                                            {/* Saturday Cells */}
                                            {satPeriod ? (
                                                <>
                                                    <TableCell align="center" sx={{ fontSize: 11.5, fontWeight: 600, borderLeft: '2px solid #aaa', bgcolor: isSatBreak ? '#fff8e1' : 'inherit', whiteSpace: 'nowrap' }}>
                                                        {satPeriod.startTime?.slice(0, 5)} to {satPeriod.endTime?.slice(0, 5)}
                                                    </TableCell>
                                                    {(() => {
                                                        const periodData = schedule['Saturday']?.[i];
                                                        const subjectVal = periodData?.subjectName || '';
                                                        const isLecture = periodData?.periodType === 'lecture';

                                                        return (
                                                            <TableCell
                                                                align="center"
                                                                sx={{
                                                                    fontSize: 12,
                                                                    fontWeight: isLecture ? 700 : 500,
                                                                    color: isLecture ? '#2c3e50' : '#d35400',
                                                                    bgcolor: !isLecture ? '#ffe0b2' : 'inherit',
                                                                    p: isLecture ? '2px !important' : 1
                                                                }}
                                                            >
                                                                {isLecture ? (
                                                                    <Box className="cell-select-wrapper" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                                        <span className="print-only-cell">{subjectVal || 'Free'}{periodData?.teacherName ? ` (${periodData.teacherName})` : ''}</span>
                                                                        <FormControl fullWidth size="small" variant="standard" className="no-print">
                                                                            <Select
                                                                                value={subjectVal}
                                                                                onChange={e => handleCellChange('Saturday', i, e.target.value)}
                                                                                disableUnderline
                                                                                sx={{
                                                                                    fontSize: 12,
                                                                                    fontWeight: 700,
                                                                                    textAlign: 'center',
                                                                                    '& .MuiSelect-select': { py: 0.25, px: 1 }
                                                                                }}
                                                                            >
                                                                                <MenuItem value="Free Period"><em>Free</em></MenuItem>
                                                                                {subjects.map(sub => (
                                                                                    <MenuItem key={sub.id} value={sub.name}>{sub.name}</MenuItem>
                                                                                ))}
                                                                            </Select>
                                                                        </FormControl>
                                                                        {subjectVal && subjectVal !== 'Free Period' && (
                                                                            <FormControl fullWidth size="small" variant="standard" className="no-print">
                                                                                <Select
                                                                                    value={periodData?.teacherName || ''}
                                                                                    onChange={e => handleTeacherChange('Saturday', i, e.target.value)}
                                                                                    disableUnderline
                                                                                    displayEmpty
                                                                                    sx={{
                                                                                        fontSize: 10,
                                                                                        color: 'text.secondary',
                                                                                        textAlign: 'center',
                                                                                        '& .MuiSelect-select': { py: 0.25, px: 1 }
                                                                                    }}
                                                                                >
                                                                                    <MenuItem value=""><em>Assign Teacher</em></MenuItem>
                                                                                    {getTeachersForSubject(subjectVal).map(t => (
                                                                                        <MenuItem key={t.id} value={`${t.user?.firstName} ${t.user?.lastName}`}>{t.user?.firstName} {t.user?.lastName}</MenuItem>
                                                                                    ))}
                                                                                </Select>
                                                                            </FormControl>
                                                                        )}
                                                                    </Box>
                                                                ) : (
                                                                    subjectVal
                                                                )}
                                                            </TableCell>
                                                        );
                                                    })()}
                                                </>
                                            ) : (
                                                <>
                                                    <TableCell sx={{ borderLeft: '2px solid #aaa', bgcolor: '#fafafa' }} />
                                                    <TableCell sx={{ bgcolor: '#fafafa' }} />
                                                </>
                                            )}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Print Signatures */}
                    <Box className="print-only" sx={{ display: 'flex', justifyContent: 'space-between', mt: 8, px: 2 }}>
                        <Box sx={{ borderTop: '1px solid #333', width: 150, textAlign: 'center', pt: 1 }}>
                            <Typography variant="body2" fontWeight={700}>Class Teacher</Typography>
                        </Box>
                        <Box sx={{ borderTop: '1px solid #333', width: 150, textAlign: 'center', pt: 1 }}>
                            <Typography variant="body2" fontWeight={700}>Supervisor</Typography>
                        </Box>
                        <Box sx={{ borderTop: '1px solid #333', width: 150, textAlign: 'center', pt: 1 }}>
                            <Typography variant="body2" fontWeight={700}>Principal</Typography>
                        </Box>
                    </Box>
                </Paper>
            ) : (
                selectedClass && selectedSection && (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Card variant="outlined" sx={{ maxWidth: 400, mx: 'auto', p: 3, bgcolor: '#fafafa' }}>
                            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                No Timetable Generated Yet
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Fill in your configurations (fixed first lectures, weekly counts) above and click "Generate & Auto Shuffle" to build the timetable.
                            </Typography>
                        </Card>
                    </Box>
                )
            )}

            {/* Custom Print Styles */}
            <style dangerouslySetInnerHTML={{ __html: `
                @media print {
                    body {
                        background-color: #ffffff !important;
                        color: #000000 !important;
                    }
                    .no-print {
                        display: none !important;
                    }
                    .print-only {
                        display: block !important;
                    }
                    .MuiPaper-root {
                        border: none !important;
                        box-shadow: none !important;
                        padding: 0 !important;
                    }
                    .print-only-cell {
                        display: inline-block !important;
                        font-weight: bold;
                    }
                    .cell-select-wrapper .no-print {
                        display: none !important;
                    }
                    table {
                        width: 100% !important;
                        border-collapse: collapse !important;
                    }
                    th, td {
                        border: 1px solid #000 !important;
                        color: #000 !important;
                        background-color: transparent !important;
                        padding: 6px !important;
                    }
                }
                .print-only {
                    display: none;
                }
                .print-only-cell {
                    display: none;
                }
            ` }} />
            <Dialog open={openCopyDialog} onClose={() => setOpenCopyDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Copy Timetable</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Copy the current timetable from Class <strong>{selectedClassName}</strong> Division <strong>{selectedSectionName}</strong> to:
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Target Class</InputLabel>
                                <Select value={copyTargetClass} label="Target Class" onChange={e => handleCopyClassChange(e.target.value)}>
                                    {classes.map(c => (
                                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small" disabled={!copyTargetClass}>
                                <InputLabel>Target Division</InputLabel>
                                <Select value={copyTargetSection} label="Target Division" onChange={e => setCopyTargetSection(e.target.value)}>
                                    {copySections.map(s => (
                                        <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCopyDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCopyTimetable} disabled={loading || !copyTargetClass || !copyTargetSection}>
                        Copy
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Timetable;
